// 📄 pages/Proyectos/BadiCatalogo.tsx
// Consulta (solo lectura) del Catálogo de BAdIs y Objetos ABAP dentro de
// un proyecto — vive en /proyecto/:projectId/badis, dentro del grupo
// "Herramientas" del sidebar. El catálogo es global (no por proyecto,
// ver catalogoBadisService.ts): el CRUD completo vive en /admin > Catálogo
// de BAdIs; acá cualquier consultor solo lo consulta/filtra/copia mientras
// trabaja en su integración. Mismo patrón que [[TransaccionesCatalogo.tsx]].
import { useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, DatabaseZap, Info } from 'lucide-react';
import { BadiObjeto, getBadis, seedCatalogoBadis } from '../../services/catalogoBadisService.ts';
import { BadiZ, getBadisZ } from '../../services/badiZService.ts';
import BadiCatalogGrid from '../../components/abap/BadiCatalogGrid.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { useProject } from '../../context/ProjectContext.tsx';

type VistaBadis = 'todas' | 'estandar' | 'z';

export default function BadiCatalogo() {
  const { profile } = useAuth();
  const esAdmin = profile?.role === 'admin';
  const { projectId } = useProject();
  const [vista, setVista] = useState<VistaBadis>('todas');
  const [badis, setBadis] = useState<BadiObjeto[]>([]);
  const [loading, setLoading] = useState(true);
  const [sembrando, setSembrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Solo un Admin tiene permiso de escritura en catalogoBadis (ver
  // firestore.rules) — un consultor común que entre acá con el catálogo
  // vacío NUNCA debe intentar sembrarlo (fallaría con permission-denied);
  // simplemente ve el estado vacío y espera a que un Admin lo cargue.
  const seedIntentado = useRef(false);

  // BAdIs Z de este proyecto — se cargan aparte (colección/servicio
  // distintos, ver ConfiguracionBadisZ.tsx donde se administran).
  const [badisZ, setBadisZ] = useState<BadiZ[]>([]);
  const [loadingZ, setLoadingZ] = useState(true);
  const [errorZ, setErrorZ] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        let data = await getBadis();
        if (data.length === 0 && esAdmin && !seedIntentado.current) {
          seedIntentado.current = true;
          setSembrando(true);
          try {
            await seedCatalogoBadis();
            data = await getBadis();
          } finally {
            setSembrando(false);
          }
        }
        setBadis(data);
      } catch (err) {
        console.error('Error cargando el catálogo de BAdIs:', err);
        setError('No se pudo cargar el catálogo de BAdIs.');
      } finally {
        setLoading(false);
      }
    })();
  }, [esAdmin]);

  useEffect(() => {
    if (!projectId) { setLoadingZ(false); return; }
    setLoadingZ(true);
    getBadisZ(projectId)
      .then(setBadisZ)
      .catch((err) => {
        console.error('Error cargando los BAdIs Z del proyecto:', err);
        setErrorZ('No se pudieron cargar los BAdIs Z de este proyecto.');
      })
      .finally(() => setLoadingZ(false));
  }, [projectId]);

  // El grid espera el shape de BadiObjeto (ver BadiCatalogGrid.tsx) — el
  // modelo Z no distingue `tipo` (siempre es un BAdI/objeto Z de este
  // proyecto), así que se fija en 'badi' acá antes de pasarlo.
  const badisZAdaptados = useMemo(
    () => badisZ.map((z) => ({
      id: z.id,
      nombre: z.nombre,
      solucion: z.solucion,
      tipo: 'badi' as const,
      transaccion: '',
      implementacionZ: '',
      claseImplementacion: z.clase,
      metodoInterfaz: z.interfaz,
      programaInclude: '',
      descripcion: z.descripcion,
      responsable: z.responsable,
      sistemasDisponibles: z.sistemasDisponibles
    })),
    [badisZ]
  );

  // El contador siempre refleja el total de Z del proyecto, sin importar el
  // filtro que esté activo DENTRO del grid de Z (ese filtro es estado local
  // de BadiCatalogGrid, no llega hasta acá).
  const zCount = badisZ.length;

  const OPCIONES_VISTA: { id: VistaBadis; label: string }[] = [
    { id: 'todas', label: 'Todas' },
    { id: 'estandar', label: 'Estándar' },
    { id: 'z', label: zCount > 0 ? `BAdIs Z (${zCount})` : 'BAdIs Z' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {OPCIONES_VISTA.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setVista(id)}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all ${
              vista === id
                ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {vista !== 'z' && (
        <>
          <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Catálogo global de referencia técnica, mantenido desde el panel de Administración. Filtra por Solución o Tipo de Objeto y usa el botón de copiar para llevarte el nombre técnico o método a SE18/SE24/SPROXY.</span>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
          )}

          {loading ? (
            <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              <p className="text-sm">{sembrando ? 'Inicializando catálogo con datos de ejemplo...' : 'Cargando catálogo...'}</p>
            </div>
          ) : badis.length === 0 ? (
            <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
              <DatabaseZap className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Todavía no hay objetos registrados en el catálogo. Un Admin puede cargarlos desde /admin.</p>
            </div>
          ) : (
            <BadiCatalogGrid badis={badis} readOnly />
          )}
        </>
      )}

      {vista !== 'estandar' && projectId && (
        <div className={vista === 'todas' ? 'pt-6 border-t border-white/10 space-y-4' : 'space-y-4'}>
          <div>
            <h3 className="text-sm font-bold text-white">BAdIs Z de este proyecto</h3>
            <p className="text-xs text-gray-500 mt-0.5">BAdIs y objetos ABAP personalizados configurados para este proyecto. Solo tú y tu equipo los ven.</p>
          </div>

          {errorZ && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{errorZ}</div>
          )}

          {loadingZ ? (
            <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              <p className="text-sm">Cargando BAdIs Z...</p>
            </div>
          ) : badisZ.length === 0 ? (
            <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
              <DatabaseZap className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Aún no hay BAdIs Z en este proyecto. Agrégalos en Configuración &gt; Desarrollo Z.</p>
            </div>
          ) : (
            <BadiCatalogGrid badis={badisZAdaptados} esZ readOnly />
          )}
        </div>
      )}
    </div>
  );
}
